import { Box, Stack, Typography } from '@mui/material'
import React from 'react'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { getToolbarShellSx, ToolbarStyle } from './toolbarStyles'

export type { ToolbarStyle }

interface ToolbarProps {
  alwaysVisible?: boolean
  contentKey?: string
  end?: React.ReactNode
  selectionActions?: React.ReactNode
  selectionCount?: number
  selectionCountLabelKey?: string
  start?: React.ReactNode
  style?: ToolbarStyle
}

const Toolbar: React.FC<ToolbarProps> = ({
  alwaysVisible = false,
  contentKey = '',
  end,
  selectionActions,
  selectionCount = 0,
  selectionCountLabelKey = 'commons.toolbar.selectedCount',
  start,
  style = 'table',
}) => {
  const t = useAppTranslations()
  const shellSx = getToolbarShellSx(style)

  if (selectionActions) {
    return (
      <Box data-testid="toolbar-shell" sx={shellSx}>
        <Box
          className="Toolbar-content"
          data-testid="toolbar-content"
          key="selection"
          sx={{
            alignItems: { md: 'center', xs: 'stretch' },
            display: 'flex',
            flexDirection: { md: 'row', xs: 'column' },
            flexWrap: { md: 'wrap', xs: 'nowrap' },
            gap: 1,
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography color="text.secondary" sx={{ order: { md: 0, xs: 1 } }} variant="body2">
            {t(selectionCountLabelKey, { count: selectionCount })}
          </Typography>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              borderTop: { md: 'none', xs: (theme) => `1px solid ${theme.palette.divider}` },
              flexWrap: 'wrap',
              gap: 0.5,
              justifyContent: { md: 'flex-end', xs: 'flex-end' },
              order: { md: 0, xs: 2 },
              paddingTop: { md: 0, xs: 0.5 },
              width: { md: 'auto', xs: '100%' },
            }}
          >
            {selectionActions}
          </Stack>
        </Box>
      </Box>
    )
  }

  if (!alwaysVisible && !start && !end) return null

  return (
    <Box data-testid="toolbar-shell" sx={shellSx}>
      <Box
        className="Toolbar-content"
        data-testid="toolbar-content"
        key={`default-${contentKey}`}
        sx={{
          alignItems: { md: 'center', xs: 'stretch' },
          display: 'flex',
          flexDirection: { md: 'row', xs: 'column' },
          flexWrap: { md: 'wrap', xs: 'nowrap' },
          gap: 1,
          justifyContent: 'space-between',
          minWidth: 0,
          width: '100%',
        }}
      >
        <Box
          sx={{
            alignItems: { md: 'center', xs: 'stretch' },
            display: 'flex',
            flex: { md: '1 1 auto', xs: '1 1 100%' },
            flexDirection: { md: 'row', xs: 'column' },
            flexWrap: 'wrap',
            gap: 1,
            minWidth: 0,
            order: { md: 0, xs: 1 },
            width: { md: 'auto', xs: '100%' },
            '& > form': {
              width: '100%',
            },
          }}
        >
          {start}
        </Box>
        {end ? (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              borderTop: { md: 'none', xs: (theme) => `1px solid ${theme.palette.divider}` },
              flex: { md: '0 1 auto', xs: '0 0 auto' },
              flexWrap: 'wrap',
              gap: 0.5,
              justifyContent: { md: 'flex-end', xs: 'flex-end' },
              marginLeft: { md: 'auto', xs: 0 },
              order: { md: 0, xs: 2 },
              paddingTop: { md: 0, xs: 0.5 },
              width: { md: 'auto', xs: '100%' },
            }}
          >
            {end}
          </Stack>
        ) : null}
      </Box>
    </Box>
  )
}

export default Toolbar
